import { AppElement, html } from '../AppElement.js';
import TableLoadingRow from "../table/TableLoadingRow";
import TableNoDataRow from "../table/TableNoDataRow";
import TableColumn from "../table/TableColumn";
import TableCell, {tableCellTypes} from "../table/TableCell";
import Constants from "../Constants";
import TableRow from "../table/TableRow";
import MiniContext from "../miniContext/MiniContext";
import VirtualAnalyst from "../virtualAnalayst/VirtualAnalyst";
import {isJSONString} from "../Utils";
import ColorArray from "../ColorArray";
import {getIdentityDependantTableStateKey} from "../TableUtils";
import Table from "../table/Table";
import {storyHtml, StoryText, storyTextTypes} from "../StoryTextElement";
import {sendRequest} from '../App.common';
import StoryInternalKpi from "../storyInternalKpi/StoryInternalKpi";
import StoryInternalKpiPace from "../storyInternalKpiPace/StoryInternalKpiPace";
import {mapCompPacePerSetRank, mapRatePacePerSetRank} from "../StoryPitchMappings";

let paceDescMap = {
    1: 'strong',
    2: 'slightly ahead of pace',
    3: 'inline with pace',
    4: 'slightly behind pace',
    5: 'behind pace'
};

// Additionally, there will be dynamic columns for the competitive set names.
export const cellFieldMap = {
    stayDate: 'stayDate',
    roomsToSell: 'roomsToSell',
    roomsOtb: 'roomsOtb',
    occupancy: 'occupancy',
    revenue: 'revenue',
    adr: 'adr',
    roomsOtbPickup: 'roomsOtbPickup',
    revenuePickup: 'revenuePickup',
    pickupsAdr: 'pickupsAdr',
    adrPickup: 'adrPickup',
    roomsOtbPace: 'roomsOtbPace',
    ratePace: 'ratePace',
    availabilityPace: 'availabilityPace',
    forecast: 'forecast',
    fcrange: 'fcrange',
    expectedPickup: 'expectedPickup',
    positioning:'positioning',
    rate: 'rate',
    compSetAvg: 'compSetAvg',
    WhatChanged: 'What Changed'
};

let requestCount = 0; // Use this to make sure that only the latest ajax request can change internal properties.

export default class PageRulesSingleTable extends AppElement {
    static get properties() {
        return {
            hotel: { type: Object },
            recordDate: { type: String },
            pickupSetting: { type: Number },
            startDate: { type: String },
            endDate: { type: String },

            // @internalProperty
            tableOpts: { attribute: false },
            columns: { attribute: false },
            rows: { attribute: false },
            pacedata: { attribute: false },
            rmsOTBdata: { attribute: false },
            forecastdata: { attribute: false },
            isLoading: { attribute: false },
            paceLoaded : {attribute : false},
            rmsOTBLoaded : {attribute : false},
            forecastLoaded : {attribute : false},
            positionLoaded : {attribute : false},
            rowData : {type: Object}
        };
    }

    constructor(props = {}) {
        super();
        this.hotel = props.hotel || this.hotel || window.infinito.vao.controller.storageHelper.getSelectedHotel();
        this.recordDate = props.recordDate
            || this.recordDate
            || window.infinito.vao.controller.storageHelper.getCurrentDate();
        this.pickupSetting = $.isNumeric(props.pickupSetting)
            ? props.pickupSetting
            : ($.isNumeric(this.pickupSetting)
                    ? this.pickupSetting
                    : window.infinito.vao.controller.storageHelper.getPickupOffset()
            );
        this.startDate = props.startDate || this.startDate;
        this.endDate = props.endDate || this.endDate;

        this.tableStateKey = getIdentityDependantTableStateKey('PageRulesSingleTable');
        this.tableOpts = {
            ...Table.getDefaultTableOpts(),
            isStickyHeader: true,
            stickyHeaderMinHeight: '74vh'
        };
        this.columns = this.makeStaticColumns();
        this.rows = [];
        this.pacedata = [];
        this.rmsOTBdata = [];
        this.forecastdata = [];
        this.isLoading = false;
        this.tableCellCache = new Map();
        this.roomsPerHousekeeper = 0;
        this.forecastLoaded = false;
        this.paceLoaded = false;
        this.positionLoaded = false;
        this.rmsOTBLoaded = false;
        this.rowData = [];
    }

    reflow(props = {}) {
        this.hotel = props.hotel || this.hotel;
        this.recordDate = props.recordDate || this.recordDate;
        this.pickupSetting = $.isNumeric(props.pickupSetting) ? props.pickupSetting : this.pickupSetting;
        this.startDate = props.startDate || this.startDate;
        this.endDate = props.endDate || this.endDate;
    }

    makeStaticColumns() {
        const columnKeyToStringMap = {
            [cellFieldMap.stayDate]: Constants.STRINGS.STAY_DATE,
            [cellFieldMap.WhatChanged]: 'What Changed',
            [cellFieldMap.roomsToSell]: 'Rms to sell',
            [cellFieldMap.expectedPickup]: 'Expected PU',
            [cellFieldMap.occupancy]: 'Occupancy',
            [cellFieldMap.forecast]: 'Forecast',
            [cellFieldMap.fcrange]: 'FC Range',
            [cellFieldMap.roomsOtbPace]: 'My Pace',
            [cellFieldMap.roomsOtb]: 'Rms',
            [cellFieldMap.revenue]: 'Rev',
            [cellFieldMap.adr]: 'ADR',
            [cellFieldMap.roomsOtbPickup]: 'Rms PU',
            [cellFieldMap.revenuePickup]: 'Rev PU',
            [cellFieldMap.pickupsAdr]: 'PU ADR',
            [cellFieldMap.rate]: 'My Rate',
            [cellFieldMap.ratePace]: 'My Price',
            [cellFieldMap.compSetAvg]: 'Comp Avg',
            [cellFieldMap.positioning]:'Positioning'
        };

        let columns = [];
        Object.keys(columnKeyToStringMap).forEach(key => {
            columns.push(new TableColumn(
                new TableCell({
                    field: key,
                    value: columnKeyToStringMap[key],
                    type: tableCellTypes.TH
                }),
                [],
                undefined,
                null,
                true,
                false
            ));
        });
        return columns;
    }

    addCompNameColumn(dynamicColumnKey, dynamicColumnText) {
        const existing = this.columns.find(column => column.tableCell.field === dynamicColumnKey);
        if (existing) {
            return;
        }

        // Sync.
        const newColumn = new TableColumn(
            new TableCell({
                field: dynamicColumnKey,
                value: dynamicColumnText,
                type: tableCellTypes.TH
            }),
            [],
            undefined,
            null,
            true,
            false
        );
        this.columns = [...this.columns, newColumn];

        // Async.
        $.when(window.infinito.vao.controller.compSetMappingHelper.fetchCompMapForCompetitor(dynamicColumnText))
            .then(mapName => {
                if (mapName && mapName !== dynamicColumnText) {
                    newColumn.tableCell.value = mapName;
                    this.columns = [...this.columns];
                }
            });
    }

    onClickStayDate(stayDate, e) {
        const prettyRecordDate = window.infinito.vao.controller.dateHelper.getShortDayDateMonFullYearString(
            this.recordDate
        );
        const prettyStayDate = window.infinito.vao.controller.dateHelper.getShortDayDateMonFullYearString(
            stayDate
        );
        const prettyPickupSetting = window.infinito.vao.controller.pickupHelper.getPickupOffsetTextForPickupOffset(
            this.pickupSetting
        );

        window.infinito.components.basicModal.render({
            title: $('<div></div>').append('<h5>Virtual Analyst</h5>').append(new MiniContext({
                recordDate: prettyRecordDate,
                stayDate: prettyStayDate,
                pickupSetting: prettyPickupSetting,
                isModalStyle: false
            })),
            isTitleHtml: true,
            bodyElem: $(new VirtualAnalyst({
                recordDate: this.recordDate,
                stayDate: stayDate,
                pickupSetting: this.pickupSetting,
                hotel: this.hotel,
            }))
        });
    }

    acquireTableCell(stayDate, props) {
        const key = `__@${props.field}_${stayDate}__`;
        if (this.tableCellCache.has(key)) {
            let existingTableCell = this.tableCellCache.get(key);
            existingTableCell.reflow(props);
            return existingTableCell;
        }
        let newTableCell = new TableCell(props);
        this.tableCellCache.set(key, newTableCell);
        return newTableCell;
    }
    acquireEmptyTableCell(stayDate, props) {
        const key = `__@${props.field}_${stayDate}__`;
        /*if (this.tableCellCache.has(key)) {
            let existingTableCell = this.tableCellCache.get(key);
            existingTableCell.reflow(props);
            return existingTableCell;
        }*/
        let newTableCell = new TableCell(props);
        this.tableCellCache.set(key, newTableCell);
        return newTableCell;
    }

    makeGradientColorCoderForMinAndMax(min, max) {
        const colorBuckets = window.infinito.vao.controller.gradientColorHelper.getColorBucketsForMinForMax(min, max);
        return function (statisticValue) {
            if (!$.isNumeric(statisticValue)) {
                return null;
            }
            const floatValue = parseFloat(statisticValue);
            if (floatValue === 0 || floatValue === 0.0) {
                return null;
            }
            return window.infinito.vao.controller.gradientColorHelper.getColorForValueForColorBuckets(
                floatValue,
                colorBuckets,
                null
            );
        }
    }
    makeGradientColorCoder(procData, key) {
        let values = [];
        let min, max;
        if (
            procData
            && typeof procData === 'object'
            && procData[key]
            && typeof procData[key] === 'object'
        ) {
            Object.values(procData[key]).forEach(statistic => {
                if (
                    statistic
                    && typeof statistic === 'object'
                    && 'value' in statistic
                    && $.isNumeric(statistic.value)
                ) {
                    values.push(parseFloat(statistic.value));
                }
            });
        }
        if (values.length <= 1) {
            // No point coloring.
            min = null;
            max = null;
        } else {
            min = Math.min(...values);
            max = Math.max(...values);
        }

        return this.makeGradientColorCoderForMinAndMax(min, max);
    }

    makeAdrMoveGradientColorCoder(procData) {
        return this.makeGradientColorCoder(procData, 'adrPickup');
    }

    makeCompSetAvgGradientColorCoder(procData) {
        let values = [];
        let min, max;
        if (
            procData
            && typeof procData === 'object'
            && procData.rate
            && typeof procData.rate === 'object'
            && procData.compAverageRate
            && typeof procData.compAverageRate === 'object'
        ) {
            Object.keys(procData.rate).forEach(stayDate => {

                const rate = procData.rate[stayDate];
                const compAverageRate = procData.compAverageRate[stayDate];
                if (
                    rate
                    && typeof rate === 'object'
                    && 'value' in rate
                    && $.isNumeric(rate.value)
                    && compAverageRate
                    && typeof compAverageRate === 'object'
                    && 'value' in compAverageRate
                    && $.isNumeric(compAverageRate.value)
                ) {
                    const diff = compAverageRate['value'];
                    values.push(diff);
                }
            });
        }
        if (values.length <= 1) {
            // No point coloring.
            min = null;
            max = null;
        } else {
            min = Math.min(...values);
            max = Math.max(...values);
        }

        return this.makeGradientColorCoderForMinAndMax(min, max);
    }

    renderPaceTag(pnn, pacePickup) {
        const positive = window.infinito.vao.controller.analyseAttributesHelper.positive;
        const negative = window.infinito.vao.controller.analyseAttributesHelper.negative;
        const neutral = window.infinito.vao.controller.analyseAttributesHelper.neutral;
        const bgColor = (pacePickup > 0) ? '#def7df' : (pacePickup < 0) ? '#ffe6e7' : null;
        let icon, iconColor;

        if (pnn === positive) {
            icon = html`<vao-icon cls="${Constants.ICONS.EMOTE_SMILE}"></vao-icon>`;
            iconColor = 'text-success';
        } else if (pnn === negative) {
            icon = html`<vao-icon cls="${Constants.ICONS.EMOTE_FROWN}"></vao-icon>`;
            iconColor = 'text-danger';
        } else if (pnn === neutral) {
            icon = html`<vao-icon cls="${Constants.ICONS.EMOTE_MEH}"></vao-icon>`;
            iconColor = 'text-info';
        } else {
            return Constants.MISSING_STR;
        }

        return html`<vao-tag .label="${icon}" labelColor="${iconColor}" bgColor="${bgColor}"></vao-tag>`;
    }

    formatPercentageValue(unformattedValue) {
        var percent = parseFloat(unformattedValue * 100);
        if (!Number.isNaN(percent)) {
            return percent.toFixed(1) + '%';
        }
        return unformattedValue;
    }

    fill() {
        document.getElementById("pacemvmt").style.backgroundColor = "#ffae01";
        document.getElementById("pacemvmt").style.borderColor = "#ffae01";
        // const dates = window.infinito.vao.controller.dateHelper.calcStayDateRangeArray(this.startDate, this.endDate);
        this.isLoading = true;
        this.rows = [];

        //RMSSOLD(LOW,MID) RANGE
        this.SendRmsSoldRequest();

        // PACE DATA
        let dataObj = {
            'hotel_id': this.hotel.id,
            'recordDate': infinito.vao.controller.storageHelper.getCurrentDate(),
            'stayDate': infinito.vao.controller.storageHelper.getCurrentDate(),
            'offset': infinito.vao.controller.storageHelper.getPickupOffset(),
            'op': 'getPaceruledata',
            'serviceName': "ivirulesapi.php"
        };
        let flag = this;
        sendRequest(dataObj, function (res) {
            //var testdata = {"status":"success","msg":"Pace Data Fetched","data":{"supportingdata":[{"stayDate":"2021-12-22","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2021-12-23","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2021-12-24","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2021-12-25","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","3"]},{"stayDate":"2021-12-26","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2021-12-27","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2021-12-28","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2021-12-29","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2021-12-30","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2021-12-31","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-01-01","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","2"]},{"stayDate":"2022-01-02","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","1"]},{"stayDate":"2022-01-03","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","1"]},{"stayDate":"2022-01-04","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-01-05","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","1"]},{"stayDate":"2022-01-06","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-01-07","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-01-08","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-01-09","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","3"]},{"stayDate":"2022-01-10","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-01-11","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-01-12","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","3"]},{"stayDate":"2022-01-13","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-01-14","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-01-15","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-01-16","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-01-17","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-01-18","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-01-19","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","2"]},{"stayDate":"2022-01-20","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-01-21","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","4"]},{"stayDate":"2022-01-22","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-01-23","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["5","5"]},{"stayDate":"2022-01-24","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["5","5"]},{"stayDate":"2022-01-25","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","5"]},{"stayDate":"2022-01-26","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","4"]},{"stayDate":"2022-01-27","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["5","5"]},{"stayDate":"2022-01-28","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["5","5"]},{"stayDate":"2022-01-29","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["5","4"]},{"stayDate":"2022-01-30","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-01-31","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-02-01","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-02-02","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-02-03","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-02-04","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-02-05","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-02-06","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-02-07","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-02-08","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-02-09","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-02-10","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","2"]},{"stayDate":"2022-02-11","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-02-12","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-02-13","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-02-14","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-02-15","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-02-16","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-02-17","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-02-18","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-02-19","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-02-20","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","1"]},{"stayDate":"2022-02-21","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-02-22","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-02-23","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","2"]},{"stayDate":"2022-02-24","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-02-25","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-02-26","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-02-27","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-02-28","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-03-01","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-02","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","2"]},{"stayDate":"2022-03-03","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-03-04","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-03-05","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-03-06","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-03-07","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-08","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-09","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-10","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-11","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-12","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-03-13","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-03-14","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-15","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","2"]},{"stayDate":"2022-03-16","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","4"]},{"stayDate":"2022-03-17","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","4"]},{"stayDate":"2022-03-18","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","2"]},{"stayDate":"2022-03-19","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","3"]},{"stayDate":"2022-03-20","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-03-21","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-22","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-23","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-24","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-03-25","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","3"]},{"stayDate":"2022-03-26","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-27","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-28","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-29","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-30","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-03-31","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-04-01","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-04-02","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-04-03","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-04-04","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-04-05","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-04-06","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-04-07","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-04-08","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["5","5"]},{"stayDate":"2022-04-09","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["5","5"]},{"stayDate":"2022-04-10","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-04-11","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-04-12","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-04-13","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-04-14","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-04-15","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-04-16","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-04-17","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-04-18","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-04-19","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-04-20","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["5","5"]},{"stayDate":"2022-04-21","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-04-22","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-04-23","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["5","5"]},{"stayDate":"2022-04-24","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["5","5"]},{"stayDate":"2022-04-25","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-04-26","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-04-27","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-04-28","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["5","5"]},{"stayDate":"2022-04-29","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["5","5"]},{"stayDate":"2022-04-30","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-05-01","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-05-02","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-05-03","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-05-04","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-05-05","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-05-06","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["5","5"]},{"stayDate":"2022-05-07","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-05-08","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-05-09","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-05-10","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-05-11","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-05-12","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-05-13","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-05-14","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-05-15","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-05-16","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-05-17","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-05-18","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-05-19","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-05-20","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-05-21","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-05-22","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-05-23","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-05-24","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-05-25","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-05-26","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-05-27","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-05-28","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-05-29","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-05-30","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-05-31","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-01","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-02","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-03","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-06-04","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-05","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-06","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-06-07","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-06-08","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-06-09","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-06-10","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-06-11","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-12","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-13","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-14","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-15","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-06-16","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-06-17","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-06-18","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-06-19","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-06-20","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-06-21","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-06-22","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-06-23","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-24","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-06-25","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-06-26","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-27","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-28","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-29","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-06-30","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-07-01","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-07-02","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-07-03","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-07-04","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-07-05","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-07-06","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-07-07","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-07-08","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-07-09","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-07-10","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-07-11","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-07-12","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-07-13","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-07-14","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-07-15","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-07-16","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-07-17","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-07-18","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-07-19","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-07-20","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-07-21","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-07-22","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-07-23","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-07-24","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-07-25","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-07-26","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-07-27","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-07-28","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-07-29","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-07-30","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-07-31","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-08-01","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-02","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-03","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-08-04","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-08-05","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-08-06","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-08-07","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-08","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-09","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-10","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-11","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-12","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-13","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-14","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-15","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-16","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-08-17","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-18","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-19","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-20","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-21","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-22","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-23","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-24","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-25","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-08-26","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-08-27","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-08-28","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-29","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-30","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-08-31","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-01","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-02","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-09-03","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-09-04","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-09-05","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-06","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-07","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-09-08","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-09","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-10","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-09-11","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-12","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-09-13","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-09-14","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-09-15","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-16","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-17","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-09-18","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-19","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-09-20","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-09-21","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-09-22","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-09-23","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-09-24","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-25","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-26","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-27","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-28","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-29","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-09-30","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-01","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-10-02","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-03","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-04","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-05","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-06","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-07","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-08","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-09","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-10-10","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-11","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-12","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-13","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-14","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-15","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-16","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-10-17","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-10-18","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-10-19","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-20","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-10-21","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-22","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-10-23","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-24","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-25","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-26","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-10-27","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-10-28","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-10-29","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","2"]},{"stayDate":"2022-10-30","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","2"]},{"stayDate":"2022-10-31","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-01","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-02","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-03","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-04","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-05","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-06","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-07","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-11-08","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-11-09","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-11-10","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-11-11","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-11-12","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-11-13","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-11-14","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-15","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-16","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-17","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-18","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-19","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-20","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-11-21","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-11-22","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-11-23","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-11-24","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-11-25","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-11-26","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-11-27","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-11-28","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-11-29","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["4","4"]},{"stayDate":"2022-11-30","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-01","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-12-02","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-03","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-04","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-05","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-06","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-07","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-08","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-12-09","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-10","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-11","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-12","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-13","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-14","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-15","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-12-16","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-17","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-18","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["3","3"]},{"stayDate":"2022-12-19","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-12-20","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-12-21","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-12-22","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-12-23","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-12-24","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-12-25","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-12-26","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-12-27","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-12-28","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-12-29","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["1","1"]},{"stayDate":"2022-12-30","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]},{"stayDate":"2022-12-31","Records_id_ss":"3429660","Records_id_os":"3422321","rank":["2","2"]}],"rankData":["2021-12-25","2022-01-01","2022-01-02","2022-01-03","2022-01-05","2022-01-09","2022-01-12","2022-01-19","2022-01-21","2022-01-25","2022-01-26","2022-01-29","2022-02-10","2022-02-20","2022-02-23","2022-03-02","2022-03-15","2022-03-16","2022-03-17","2022-03-18","2022-03-19","2022-03-25","2022-10-29","2022-10-30"],"pmsvalues":{"snapshotdata":{"roomsOtb":{"2021-12-25":{"field":"roomsOtb","stayDate":"2021-12-25","value":"46"},"2022-01-01":{"field":"roomsOtb","stayDate":"2022-01-01","value":"48"},"2022-01-02":{"field":"roomsOtb","stayDate":"2022-01-02","value":"42"},"2022-01-03":{"field":"roomsOtb","stayDate":"2022-01-03","value":"42"},"2022-01-05":{"field":"roomsOtb","stayDate":"2022-01-05","value":"41"},"2022-01-09":{"field":"roomsOtb","stayDate":"2022-01-09","value":"37"},"2022-01-12":{"field":"roomsOtb","stayDate":"2022-01-12","value":"33"},"2022-01-19":{"field":"roomsOtb","stayDate":"2022-01-19","value":"34"},"2022-01-21":{"field":"roomsOtb","stayDate":"2022-01-21","value":"41"},"2022-01-25":{"field":"roomsOtb","stayDate":"2022-01-25","value":"16"},"2022-01-26":{"field":"roomsOtb","stayDate":"2022-01-26","value":"19"},"2022-01-29":{"field":"roomsOtb","stayDate":"2022-01-29","value":"24"},"2022-02-10":{"field":"roomsOtb","stayDate":"2022-02-10","value":"17"},"2022-02-20":{"field":"roomsOtb","stayDate":"2022-02-20","value":"11"},"2022-02-23":{"field":"roomsOtb","stayDate":"2022-02-23","value":"10"},"2022-03-02":{"field":"roomsOtb","stayDate":"2022-03-02","value":"8"},"2022-03-15":{"field":"roomsOtb","stayDate":"2022-03-15","value":"7"},"2022-03-16":{"field":"roomsOtb","stayDate":"2022-03-16","value":"5"},"2022-03-17":{"field":"roomsOtb","stayDate":"2022-03-17","value":"6"},"2022-03-18":{"field":"roomsOtb","stayDate":"2022-03-18","value":"15"},"2022-03-19":{"field":"roomsOtb","stayDate":"2022-03-19","value":"15"},"2022-03-25":{"field":"roomsOtb","stayDate":"2022-03-25","value":"13"},"2022-10-29":{"field":"roomsOtb","stayDate":"2022-10-29","value":"0"},"2022-10-30":{"field":"roomsOtb","stayDate":"2022-10-30","value":"0"}},"roomsToSell":{"2021-12-25":{"field":"roomsToSell","stayDate":"2021-12-25","value":-29},"2022-01-01":{"field":"roomsToSell","stayDate":"2022-01-01","value":-31},"2022-01-02":{"field":"roomsToSell","stayDate":"2022-01-02","value":-25},"2022-01-03":{"field":"roomsToSell","stayDate":"2022-01-03","value":-25},"2022-01-05":{"field":"roomsToSell","stayDate":"2022-01-05","value":-24},"2022-01-09":{"field":"roomsToSell","stayDate":"2022-01-09","value":-20},"2022-01-12":{"field":"roomsToSell","stayDate":"2022-01-12","value":-16},"2022-01-19":{"field":"roomsToSell","stayDate":"2022-01-19","value":-17},"2022-01-21":{"field":"roomsToSell","stayDate":"2022-01-21","value":-24},"2022-01-25":{"field":"roomsToSell","stayDate":"2022-01-25","value":1},"2022-01-26":{"field":"roomsToSell","stayDate":"2022-01-26","value":-2},"2022-01-29":{"field":"roomsToSell","stayDate":"2022-01-29","value":-7},"2022-02-10":{"field":"roomsToSell","stayDate":"2022-02-10","value":0},"2022-02-20":{"field":"roomsToSell","stayDate":"2022-02-20","value":6},"2022-02-23":{"field":"roomsToSell","stayDate":"2022-02-23","value":7},"2022-03-02":{"field":"roomsToSell","stayDate":"2022-03-02","value":9},"2022-03-15":{"field":"roomsToSell","stayDate":"2022-03-15","value":10},"2022-03-16":{"field":"roomsToSell","stayDate":"2022-03-16","value":12},"2022-03-17":{"field":"roomsToSell","stayDate":"2022-03-17","value":11},"2022-03-18":{"field":"roomsToSell","stayDate":"2022-03-18","value":2},"2022-03-19":{"field":"roomsToSell","stayDate":"2022-03-19","value":2},"2022-03-25":{"field":"roomsToSell","stayDate":"2022-03-25","value":4},"2022-10-29":{"field":"roomsToSell","stayDate":"2022-10-29","value":14},"2022-10-30":{"field":"roomsToSell","stayDate":"2022-10-30","value":14}},"revenueNet":{"2021-12-25":{"field":"revenueNet","stayDate":"2021-12-25","value":"10074.3100"},"2022-01-01":{"field":"revenueNet","stayDate":"2022-01-01","value":"13243.9700"},"2022-01-02":{"field":"revenueNet","stayDate":"2022-01-02","value":"9361.7800"},"2022-01-03":{"field":"revenueNet","stayDate":"2022-01-03","value":"8768.2800"},"2022-01-05":{"field":"revenueNet","stayDate":"2022-01-05","value":"7674.9300"},"2022-01-09":{"field":"revenueNet","stayDate":"2022-01-09","value":"6695.6000"},"2022-01-12":{"field":"revenueNet","stayDate":"2022-01-12","value":"6321.4600"},"2022-01-19":{"field":"revenueNet","stayDate":"2022-01-19","value":"5991.2100"},"2022-01-21":{"field":"revenueNet","stayDate":"2022-01-21","value":"8603.2100"},"2022-01-25":{"field":"revenueNet","stayDate":"2022-01-25","value":"1914.3300"},"2022-01-26":{"field":"revenueNet","stayDate":"2022-01-26","value":"2322.6700"},"2022-01-29":{"field":"revenueNet","stayDate":"2022-01-29","value":"3591.7000"},"2022-02-10":{"field":"revenueNet","stayDate":"2022-02-10","value":"2163.6000"},"2022-02-20":{"field":"revenueNet","stayDate":"2022-02-20","value":"1232.2600"},"2022-02-23":{"field":"revenueNet","stayDate":"2022-02-23","value":"1471.8600"},"2022-03-02":{"field":"revenueNet","stayDate":"2022-03-02","value":"755.6100"},"2022-03-15":{"field":"revenueNet","stayDate":"2022-03-15","value":"775.2400"},"2022-03-16":{"field":"revenueNet","stayDate":"2022-03-16","value":"281.7400"},"2022-03-17":{"field":"revenueNet","stayDate":"2022-03-17","value":"442.9300"},"2022-03-18":{"field":"revenueNet","stayDate":"2022-03-18","value":"2345.0000"},"2022-03-19":{"field":"revenueNet","stayDate":"2022-03-19","value":"2315.0000"},"2022-03-25":{"field":"revenueNet","stayDate":"2022-03-25","value":"2473.6000"},"2022-10-29":{"field":"revenueNet","stayDate":"2022-10-29","value":"0.0000"},"2022-10-30":{"field":"revenueNet","stayDate":"2022-10-30","value":"0.0000"}},"adrNet":{"2021-12-25":{"field":"adrNet","stayDate":"2021-12-25","value":"949.3984"},"2022-01-01":{"field":"adrNet","stayDate":"2022-01-01","value":"1042.1384"},"2022-01-02":{"field":"adrNet","stayDate":"2022-01-02","value":"804.3578"},"2022-01-03":{"field":"adrNet","stayDate":"2022-01-03","value":"769.4755"},"2022-01-05":{"field":"adrNet","stayDate":"2022-01-05","value":"739.9529"},"2022-01-09":{"field":"adrNet","stayDate":"2022-01-09","value":"325.9366"},"2022-01-12":{"field":"adrNet","stayDate":"2022-01-12","value":"723.3104"},"2022-01-19":{"field":"adrNet","stayDate":"2022-01-19","value":"659.9214"},"2022-01-21":{"field":"adrNet","stayDate":"2022-01-21","value":"844.4154"},"2022-01-25":{"field":"adrNet","stayDate":"2022-01-25","value":"270.1048"},"2022-01-26":{"field":"adrNet","stayDate":"2022-01-26","value":"269.8423"},"2022-01-29":{"field":"adrNet","stayDate":"2022-01-29","value":"689.7533"},"2022-02-10":{"field":"adrNet","stayDate":"2022-02-10","value":"279.2455"},"2022-02-20":{"field":"adrNet","stayDate":"2022-02-20","value":"253.5242"},"2022-02-23":{"field":"adrNet","stayDate":"2022-02-23","value":"294.3720"},"2022-03-02":{"field":"adrNet","stayDate":"2022-03-02","value":"213.4217"},"2022-03-15":{"field":"adrNet","stayDate":"2022-03-15","value":"283.3190"},"2022-03-16":{"field":"adrNet","stayDate":"2022-03-16","value":"129.3625"},"2022-03-17":{"field":"adrNet","stayDate":"2022-03-17","value":"151.4420"},"2022-03-18":{"field":"adrNet","stayDate":"2022-03-18","value":"304.9000"},"2022-03-19":{"field":"adrNet","stayDate":"2022-03-19","value":"301.1500"},"2022-03-25":{"field":"adrNet","stayDate":"2022-03-25","value":"719.6420"},"2022-10-29":{"field":"adrNet","stayDate":"2022-10-29","value":"0.0000"},"2022-10-30":{"field":"adrNet","stayDate":"2022-10-30","value":"0.0000"}},"occupancy":{"2021-12-25":{"field":"occupancy","stayDate":"2021-12-25","value":"2.3767"},"2022-01-01":{"field":"occupancy","stayDate":"2022-01-01","value":"2.8824"},"2022-01-02":{"field":"occupancy","stayDate":"2022-01-02","value":"2.0882"},"2022-01-03":{"field":"occupancy","stayDate":"2022-01-03","value":"2.0882"},"2022-01-05":{"field":"occupancy","stayDate":"2022-01-05","value":"2.1622"},"2022-01-09":{"field":"occupancy","stayDate":"2022-01-09","value":"1.5124"},"2022-01-12":{"field":"occupancy","stayDate":"2022-01-12","value":"2.2125"},"2022-01-19":{"field":"occupancy","stayDate":"2022-01-19","value":"1.8833"},"2022-01-21":{"field":"occupancy","stayDate":"2022-01-21","value":"2.6566"},"2022-01-25":{"field":"occupancy","stayDate":"2022-01-25","value":"0.8615"},"2022-01-26":{"field":"occupancy","stayDate":"2022-01-26","value":"1.0380"},"2022-01-29":{"field":"occupancy","stayDate":"2022-01-29","value":"2.1347"},"2022-02-10":{"field":"occupancy","stayDate":"2022-02-10","value":"0.8346"},"2022-02-20":{"field":"occupancy","stayDate":"2022-02-20","value":"0.5644"},"2022-02-23":{"field":"occupancy","stayDate":"2022-02-23","value":"0.4503"},"2022-03-02":{"field":"occupancy","stayDate":"2022-03-02","value":"0.4154"},"2022-03-15":{"field":"occupancy","stayDate":"2022-03-15","value":"0.3566"},"2022-03-16":{"field":"occupancy","stayDate":"2022-03-16","value":"0.2665"},"2022-03-17":{"field":"occupancy","stayDate":"2022-03-17","value":"0.3253"},"2022-03-18":{"field":"occupancy","stayDate":"2022-03-18","value":"0.6618"},"2022-03-19":{"field":"occupancy","stayDate":"2022-03-19","value":"0.6618"},"2022-03-25":{"field":"occupancy","stayDate":"2022-03-25","value":"1.4816"},"2022-10-29":{"field":"occupancy","stayDate":"2022-10-29","value":"0.0000"},"2022-10-30":{"field":"occupancy","stayDate":"2022-10-30","value":"0.0000"}}},"offsetdata":{"roomsOtb":{"2021-12-25":{"field":"roomsOtb","stayDate":"2021-12-25","value":1},"2022-01-01":{"field":"roomsOtb","stayDate":"2022-01-01","value":1},"2022-01-02":{"field":"roomsOtb","stayDate":"2022-01-02","value":1},"2022-01-03":{"field":"roomsOtb","stayDate":"2022-01-03","value":2},"2022-01-05":{"field":"roomsOtb","stayDate":"2022-01-05","value":3},"2022-01-09":{"field":"roomsOtb","stayDate":"2022-01-09","value":2},"2022-01-12":{"field":"roomsOtb","stayDate":"2022-01-12","value":3},"2022-01-19":{"field":"roomsOtb","stayDate":"2022-01-19","value":2},"2022-01-21":{"field":"roomsOtb","stayDate":"2022-01-21","value":2},"2022-01-25":{"field":"roomsOtb","stayDate":"2022-01-25","value":2},"2022-01-26":{"field":"roomsOtb","stayDate":"2022-01-26","value":2},"2022-01-29":{"field":"roomsOtb","stayDate":"2022-01-29","value":2},"2022-02-10":{"field":"roomsOtb","stayDate":"2022-02-10","value":1},"2022-02-20":{"field":"roomsOtb","stayDate":"2022-02-20","value":1},"2022-02-23":{"field":"roomsOtb","stayDate":"2022-02-23","value":1},"2022-03-02":{"field":"roomsOtb","stayDate":"2022-03-02","value":1},"2022-03-15":{"field":"roomsOtb","stayDate":"2022-03-15","value":1},"2022-03-16":{"field":"roomsOtb","stayDate":"2022-03-16","value":1},"2022-03-17":{"field":"roomsOtb","stayDate":"2022-03-17","value":1},"2022-03-18":{"field":"roomsOtb","stayDate":"2022-03-18","value":1},"2022-03-19":{"field":"roomsOtb","stayDate":"2022-03-19","value":1},"2022-03-25":{"field":"roomsOtb","stayDate":"2022-03-25","value":1},"2022-10-29":{"field":"roomsOtb","stayDate":"2022-10-29","value":0},"2022-10-30":{"field":"roomsOtb","stayDate":"2022-10-30","value":0}},"roomsToSell":{"2021-12-25":{"field":"roomsToSell","stayDate":"2021-12-25","value":-28},"2022-01-01":{"field":"roomsToSell","stayDate":"2022-01-01","value":-30},"2022-01-02":{"field":"roomsToSell","stayDate":"2022-01-02","value":-24},"2022-01-03":{"field":"roomsToSell","stayDate":"2022-01-03","value":-23},"2022-01-05":{"field":"roomsToSell","stayDate":"2022-01-05","value":-21},"2022-01-09":{"field":"roomsToSell","stayDate":"2022-01-09","value":-18},"2022-01-12":{"field":"roomsToSell","stayDate":"2022-01-12","value":-13},"2022-01-19":{"field":"roomsToSell","stayDate":"2022-01-19","value":-15},"2022-01-21":{"field":"roomsToSell","stayDate":"2022-01-21","value":-22},"2022-01-25":{"field":"roomsToSell","stayDate":"2022-01-25","value":3},"2022-01-26":{"field":"roomsToSell","stayDate":"2022-01-26","value":0},"2022-01-29":{"field":"roomsToSell","stayDate":"2022-01-29","value":-5},"2022-02-10":{"field":"roomsToSell","stayDate":"2022-02-10","value":1},"2022-02-20":{"field":"roomsToSell","stayDate":"2022-02-20","value":7},"2022-02-23":{"field":"roomsToSell","stayDate":"2022-02-23","value":8},"2022-03-02":{"field":"roomsToSell","stayDate":"2022-03-02","value":10},"2022-03-15":{"field":"roomsToSell","stayDate":"2022-03-15","value":11},"2022-03-16":{"field":"roomsToSell","stayDate":"2022-03-16","value":13},"2022-03-17":{"field":"roomsToSell","stayDate":"2022-03-17","value":12},"2022-03-18":{"field":"roomsToSell","stayDate":"2022-03-18","value":3},"2022-03-19":{"field":"roomsToSell","stayDate":"2022-03-19","value":3},"2022-03-25":{"field":"roomsToSell","stayDate":"2022-03-25","value":5},"2022-10-29":{"field":"roomsToSell","stayDate":"2022-10-29","value":14},"2022-10-30":{"field":"roomsToSell","stayDate":"2022-10-30","value":14}},"revenueNet":{"2021-12-25":{"field":"revenueNet","stayDate":"2021-12-25","value":139.64999999999964},"2022-01-01":{"field":"revenueNet","stayDate":"2022-01-01","value":289},"2022-01-02":{"field":"revenueNet","stayDate":"2022-01-02","value":279},"2022-01-03":{"field":"revenueNet","stayDate":"2022-01-03","value":320.10000000000036},"2022-01-05":{"field":"revenueNet","stayDate":"2022-01-05","value":475.5},"2022-01-09":{"field":"revenueNet","stayDate":"2022-01-09","value":326.07999999999993},"2022-01-12":{"field":"revenueNet","stayDate":"2022-01-12","value":747.23000000000047},"2022-01-19":{"field":"revenueNet","stayDate":"2022-01-19","value":105.85000000000036},"2022-01-21":{"field":"revenueNet","stayDate":"2022-01-21","value":241.38999999999942},"2022-01-25":{"field":"revenueNet","stayDate":"2022-01-25","value":240.32999999999993},"2022-01-26":{"field":"revenueNet","stayDate":"2022-01-26","value":249.97000000000025},"2022-01-29":{"field":"revenueNet","stayDate":"2022-01-29","value":239.9699999999998},"2022-02-10":{"field":"revenueNet","stayDate":"2022-02-10","value":112.25},"2022-02-20":{"field":"revenueNet","stayDate":"2022-02-20","value":112.34999999999991},"2022-02-23":{"field":"revenueNet","stayDate":"2022-02-23","value":113.66999999999985},"2022-03-02":{"field":"revenueNet","stayDate":"2022-03-02","value":113.66999999999996},"2022-03-15":{"field":"revenueNet","stayDate":"2022-03-15","value":113.66999999999996},"2022-03-16":{"field":"revenueNet","stayDate":"2022-03-16","value":113.67000000000002},"2022-03-17":{"field":"revenueNet","stayDate":"2022-03-17","value":113.67000000000002},"2022-03-18":{"field":"revenueNet","stayDate":"2022-03-18","value":113.67000000000007},"2022-03-19":{"field":"revenueNet","stayDate":"2022-03-19","value":113.67000000000007},"2022-03-25":{"field":"revenueNet","stayDate":"2022-03-25","value":113.67000000000007},"2022-10-29":{"field":"revenueNet","stayDate":"2022-10-29","value":0},"2022-10-30":{"field":"revenueNet","stayDate":"2022-10-30","value":0}},"adrNet":{"2021-12-25":{"field":"adrNet","stayDate":"2021-12-25","value":139.65000000000001},"2022-01-01":{"field":"adrNet","stayDate":"2022-01-01","value":289},"2022-01-02":{"field":"adrNet","stayDate":"2022-01-02","value":279},"2022-01-03":{"field":"adrNet","stayDate":"2022-01-03","value":160.05000000000001},"2022-01-05":{"field":"adrNet","stayDate":"2022-01-05","value":158.5},"2022-01-09":{"field":"adrNet","stayDate":"2022-01-09","value":163.03999999999999},"2022-01-12":{"field":"adrNet","stayDate":"2022-01-12","value":249.08000000000001},"2022-01-19":{"field":"adrNet","stayDate":"2022-01-19","value":52.93},"2022-01-21":{"field":"adrNet","stayDate":"2022-01-21","value":120.7},"2022-01-25":{"field":"adrNet","stayDate":"2022-01-25","value":120.17},"2022-01-26":{"field":"adrNet","stayDate":"2022-01-26","value":124.98999999999999},"2022-01-29":{"field":"adrNet","stayDate":"2022-01-29","value":119.98999999999999},"2022-02-10":{"field":"adrNet","stayDate":"2022-02-10","value":112.25},"2022-02-20":{"field":"adrNet","stayDate":"2022-02-20","value":112.34999999999999},"2022-02-23":{"field":"adrNet","stayDate":"2022-02-23","value":113.67},"2022-03-02":{"field":"adrNet","stayDate":"2022-03-02","value":113.67},"2022-03-15":{"field":"adrNet","stayDate":"2022-03-15","value":113.67},"2022-03-16":{"field":"adrNet","stayDate":"2022-03-16","value":113.67},"2022-03-17":{"field":"adrNet","stayDate":"2022-03-17","value":113.67},"2022-03-18":{"field":"adrNet","stayDate":"2022-03-18","value":113.67},"2022-03-19":{"field":"adrNet","stayDate":"2022-03-19","value":113.67},"2022-03-25":{"field":"adrNet","stayDate":"2022-03-25","value":113.67},"2022-10-29":{"field":"adrNet","stayDate":"2022-10-29","value":0},"2022-10-30":{"field":"adrNet","stayDate":"2022-10-30","value":0}},"occupancy":{"2021-12-25":{"field":"occupancy","stayDate":"2021-12-25","value":"2.3444"},"2022-01-01":{"field":"occupancy","stayDate":"2022-01-01","value":"2.8235"},"2022-01-02":{"field":"occupancy","stayDate":"2022-01-02","value":"2.0294"},"2022-01-03":{"field":"occupancy","stayDate":"2022-01-03","value":"1.9706"},"2022-01-05":{"field":"occupancy","stayDate":"2022-01-05","value":"1.9858"},"2022-01-09":{"field":"occupancy","stayDate":"2022-01-09","value":"1.3947"},"2022-01-12":{"field":"occupancy","stayDate":"2022-01-12","value":"2.0892"},"2022-01-19":{"field":"occupancy","stayDate":"2022-01-19","value":"2.2334"},"2022-01-21":{"field":"occupancy","stayDate":"2022-01-21","value":"2.5389"},"2022-01-25":{"field":"occupancy","stayDate":"2022-01-25","value":"0.7439"},"2022-01-26":{"field":"occupancy","stayDate":"2022-01-26","value":"0.9203"},"2022-01-29":{"field":"occupancy","stayDate":"2022-01-29","value":"2.0437"},"2022-02-10":{"field":"occupancy","stayDate":"2022-02-10","value":"0.7757"},"2022-02-20":{"field":"occupancy","stayDate":"2022-02-20","value":"0.5056"},"2022-02-23":{"field":"occupancy","stayDate":"2022-02-23","value":"0.3915"},"2022-03-02":{"field":"occupancy","stayDate":"2022-03-02","value":"0.3566"},"2022-03-15":{"field":"occupancy","stayDate":"2022-03-15","value":"0.2978"},"2022-03-16":{"field":"occupancy","stayDate":"2022-03-16","value":"0.2077"},"2022-03-17":{"field":"occupancy","stayDate":"2022-03-17","value":"0.2665"},"2022-03-18":{"field":"occupancy","stayDate":"2022-03-18","value":"0.6029"},"2022-03-19":{"field":"occupancy","stayDate":"2022-03-19","value":"0.6029"},"2022-03-25":{"field":"occupancy","stayDate":"2022-03-25","value":"1.4228"},"2022-10-29":{"field":"occupancy","stayDate":"2022-10-29","value":"0.0000"},"2022-10-30":{"field":"occupancy","stayDate":"2022-10-30","value":"0.0000"}}}}}};

            var jsonObj = JSON.parse(res);//testdata;
            const dates = jsonObj['data'].rankData;
            const pmsdatasnapshotdata = jsonObj['data']['pmsvalues'].snapshotdata;
            const pmsdatasnapoffsetdata = jsonObj['data']['pmsvalues'].offsetdata;
            const marketvaluesdata = jsonObj['data']['marketvalues'];
            let rows = [];
            const compSetAvgColorCoder = flag.makeCompSetAvgGradientColorCoder(marketvaluesdata);

            let wccnt = 0;
            dates.forEach(stayDate => {

                let rowCells = [];
                let hr = flag.fetchPaceWhatChanged(stayDate+"_P",rowCells,wccnt);
                wccnt++;
                const prettyStayDate = window.infinito.vao.controller.dateHelper.getShortDayDateMonFullYearString(stayDate);

                rowCells.push(flag.acquireTableCell(stayDate+"_P" , {
                    field: cellFieldMap.stayDate,
                    value: html`
                        <vao-button
                                text="${prettyStayDate}"
                                variant="invert"
                                size="xs"
                                @click=${flag.onClickStayDate.bind(flag, stayDate)}
                        ></vao-button>
                    `
                }));

                if (
                    pmsdatasnapshotdata.roomsToSell
                    && pmsdatasnapshotdata.roomsToSell[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsToSell[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsToSell[stayDate].value)
                ) {
                    let value = parseInt(pmsdatasnapshotdata.roomsToSell[stayDate].value, 10);
                    let labelColor = null;
                    if (value !== 0) {
                        //value *= -1; // Because Fabian dont wants the inverse rendered.
                        if (value < 0) {
                            // There actually is rooms still left to sell.
                            labelColor = Constants.COLORS.HEX.DANGER;
                        }
                    }
                    rowCells.push(flag.acquireTableCell(stayDate, {
                        field: cellFieldMap.roomsToSell,
                        value: html`
                            <vao-tag
                                    .label="${value}"
                                    .labelColor="${labelColor}"
                                    canInterpretBgColor="false"
                            ></vao-tag>
                        `
                    }));
                }

                if (
                    pmsdatasnapshotdata.roomsOtb
                    && pmsdatasnapshotdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsOtb[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate, {
                        field: cellFieldMap.roomsOtb,
                        value: pmsdatasnapshotdata.roomsOtb[stayDate].value
                    }));

                }

                if (
                    pmsdatasnapshotdata.roomsOtb
                    && pmsdatasnapshotdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsOtb[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate, {
                        field: cellFieldMap.forecast,
                        value: html`
                            <vao-story-forecast-subtitle
                                    .recordDate="${flag.recordDate}"
                                    .stayDate="${stayDate}"
                                    .hotel="${flag.hotel}"
                            ></vao-story-forecast-subtitle>
                        `
                    }));
                }
                if (
                    pmsdatasnapshotdata.roomsOtb
                    && pmsdatasnapshotdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsOtb[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate, {
                        field: cellFieldMap.fcrange,
                        value: html`
                            <vao-story-forecastrange-subtitle
                                    .recordDate="${flag.recordDate}"
                                    .stayDate="${stayDate}"
                                    .hotel="${flag.hotel}"
                            ></vao-story-forecastrange-subtitle>
                        `
                    }));
                }
                if (
                    pmsdatasnapshotdata.roomsOtb
                    && pmsdatasnapshotdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsOtb[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate, {
                        field: cellFieldMap.expectedPickup,
                        value: html`
                            <vao-story-what-to-expect-pickup-subtitle
                                    .recordDate="${flag.recordDate}"
                                    .stayDate="${stayDate}"
                                    .hotel="${flag.hotel}"
                            ></vao-story-what-to-expect-pickup-subtitle>
                        `
                    }));
                }

                if (
                    pmsdatasnapshotdata.occupancy
                    && pmsdatasnapshotdata.occupancy[stayDate]
                    && 'value' in pmsdatasnapshotdata.occupancy[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.occupancy[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate, {
                        field: cellFieldMap.occupancy,
                        value: flag.formatPercentageValue(pmsdatasnapshotdata.occupancy[stayDate].value)
                    }));
                }


                if (
                    pmsdatasnapshotdata.revenueNet
                    && pmsdatasnapshotdata.revenueNet[stayDate]
                    && 'value' in pmsdatasnapshotdata.revenueNet[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.revenueNet[stayDate].value)
                ) {
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        pmsdatasnapshotdata.revenueNet[stayDate].value,
                        Constants.REVENUE_DIGITS,
                        true,
                        flag.hotel.locale
                    );
                    rowCells.push(flag.acquireTableCell(stayDate, {
                        field: cellFieldMap.revenue,
                        value: formatted
                    }));
                }

                if (
                    pmsdatasnapshotdata.adrNet
                    && pmsdatasnapshotdata.adrNet[stayDate]
                    && 'value' in pmsdatasnapshotdata.adrNet[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.adrNet[stayDate].value)
                ) {
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        pmsdatasnapshotdata.adrNet[stayDate].value,
                        Constants.ADR_DIGITS,
                        true,
                        flag.hotel.locale,
                        1
                    );

                    rowCells.push(flag.acquireTableCell(stayDate, {
                        field: cellFieldMap.adr,
                        value: formatted
                    }));
                }

                if (
                    pmsdatasnapoffsetdata.roomsOtb
                    && pmsdatasnapoffsetdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapoffsetdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapoffsetdata.roomsOtb[stayDate].value)
                ) {
                    const value = pmsdatasnapoffsetdata.roomsOtb[stayDate].value;
                    const valueInt = parseInt(value, 10);
                    const color = window.infinito.vao.controller.pickupHelper.getPickupColorFor(valueInt);
                    let bgColor = null;
                    if (color) {
                        bgColor = ColorArray.hexToRGBA(color, .15);
                    }
                    const numVarBlock = html`
                        <vao-numeric-variance-block
                                numericValue="${value}"
                                formattedValue="${value}"
                        ></vao-numeric-variance-block>
                    `;
                    rowCells.push(flag.acquireTableCell(stayDate, {
                        field: cellFieldMap.roomsOtbPickup,
                        value: html`
                            <vao-tag
                                    .label="${numVarBlock}"
                                    bgColor="${bgColor}"
                            ></vao-tag>
                        `
                    }));
                }

                if (
                    pmsdatasnapoffsetdata.revenueNet
                    && pmsdatasnapoffsetdata.revenueNet[stayDate]
                    && 'value' in pmsdatasnapoffsetdata.revenueNet[stayDate]
                    && $.isNumeric(pmsdatasnapoffsetdata.revenueNet[stayDate].value)
                ) {
                    const value = pmsdatasnapoffsetdata.revenueNet[stayDate].value;
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        value,
                        Constants.REVENUE_DIGITS,
                        true,
                        flag.hotel.locale
                    );
                    rowCells.push(flag.acquireTableCell(stayDate, {
                        field: cellFieldMap.revenuePickup,
                        value: html`
                            <vao-numeric-variance-block
                                    numericValue="${value}"
                                    formattedValue="${formatted}"
                            ></vao-numeric-variance-block>
                        `
                    }));
                }

                if (
                    pmsdatasnapoffsetdata.adrNet
                    && pmsdatasnapoffsetdata.adrNet[stayDate]
                    && 'value' in pmsdatasnapoffsetdata.adrNet[stayDate]
                    && $.isNumeric(pmsdatasnapoffsetdata.adrNet[stayDate].value)
                ) {
                    const value = pmsdatasnapoffsetdata.adrNet[stayDate].value;
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        value,
                        Constants.ADR_DIGITS,
                        true,
                        flag.hotel.locale,
                        1
                    );
                    rowCells.push(flag.acquireTableCell(stayDate, {
                        field: cellFieldMap.pickupsAdr,
                        value: html`
                            <vao-numeric-variance-block
                                    numericValue="${value}"
                                    formattedValue="${formatted}"
                            ></vao-numeric-variance-block>
                        `
                    }));
                }

                /******What Changed********/
                rowCells.push(flag.acquireTableCell(stayDate+"_P", {
                    field: cellFieldMap.WhatChanged,
                    value: ''
                }));

                /********MY PACE**********/
                let mypace = flag.fetchmypace(stayDate+"_P",flag.recordDate,rowCells);
                rowCells.push(flag.acquireTableCell(stayDate+"_P", {
                    field: cellFieldMap.roomsOtbPace,
                    value: ''
                }));

                /********MY PIRCE*********/
                let myprice = flag.fetchMyprice(stayDate+"_P",flag.recordDate,rowCells);
                rowCells.push(flag.acquireTableCell(stayDate+"_P", {
                    field: cellFieldMap.ratePace,
                    value: '',
                }));


                let normality = flag.fetchPosition(stayDate,flag.recordDate,rowCells);
                rowCells.push(flag.acquireTableCell(stayDate, {
                    field: cellFieldMap.positioning,
                    value: ''
                }));

                /**************RATE COLUMN************/
                if (
                    marketvaluesdata.rate
                    && marketvaluesdata.rate[stayDate]
                    && 'value' in marketvaluesdata.rate[stayDate]
                    /*&& $.isNumeric(marketvaluesdata.rate[stayDate].value)*/
                ) {
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        marketvaluesdata.rate[stayDate].value,
                        Constants.RATE_DIGITS,
                        true,
                        flag.hotel.locale
                    );
                    rowCells.push(flag.acquireTableCell(stayDate, {
                        field: cellFieldMap.rate,
                        value: formatted
                    }));
                }
                /**************COMP AVG COLUMN************/
                if (
                    marketvaluesdata.compAverageRate
                    && marketvaluesdata.compAverageRate[stayDate]
                    && 'value' in marketvaluesdata.compAverageRate[stayDate]
                    && $.isNumeric(marketvaluesdata.compAverageRate[stayDate].value)
                ) {
                    const diff = marketvaluesdata.compAverageRate[stayDate].value;

                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        diff,
                        Constants.RATE_DIGITS,
                        true,
                        flag.hotel.locale
                    );
                    const bgColor = compSetAvgColorCoder(diff);
                    /*bgColor="${bgColor}"*/
                    rowCells.push(flag.acquireTableCell(stayDate, {
                        field: cellFieldMap.compSetAvg,
                        value: html`
                            <vao-tag
                                    label="${formatted}"
                            ></vao-tag>
                        `
                    }));
                }

                rows.push(new TableRow(rowCells));
                flag.pacedata.push(new TableRow(rowCells));
            });

            flag.mergeData('pace',flag.pacedata);

        });

        // FORECAST DATA
        this.SendForecastRequest();

    }
    SendRmsSoldRequest(){
        document.getElementById("occmvnt").style.backgroundColor = "#ffae01";
        document.getElementById("occmvnt").style.borderColor = "#ffae01";
        var lowdemand = (infinito.vao.controller.storageHelper.isInSingleMode())
            ? infinito.vao.model.vaoSettings.obtainHotelIdLowDemandKey(
                infinito.vao.controller.storageHelper.getSelectedHotelId()
            )
            : infinito.vao.model.vaoSettings.obtainGroupIdLowDemandKey(
                infinito.vao.controller.storageHelper.getSelectedGroupId()
            );
        var mediumdemand = (infinito.vao.controller.storageHelper.isInSingleMode())
            ? infinito.vao.model.vaoSettings.obtainHotelIdMedDemandKey(
                infinito.vao.controller.storageHelper.getSelectedHotelId()
            )
            : infinito.vao.model.vaoSettings.obtainGroupIdMedDemandKey(
                infinito.vao.controller.storageHelper.getSelectedGroupId()
            );

        let dataObj = {
            'hotel_id': this.hotel.id,
            'recordDate': infinito.vao.controller.storageHelper.getCurrentDate(),
            'stayDate': infinito.vao.controller.storageHelper.getCurrentDate(),
            'offset': infinito.vao.controller.storageHelper.getPickupOffset(),
            'identity_id': infinito.vao.controller.storageHelper.getIdentitiesId(),
            'lowdemand':lowdemand,
            'mediumdemand':mediumdemand,
            'op': 'getRmsSoldruledata',
            'serviceName': "ivirulesapi.php"
        };

        let flag = this;
        sendRequest(dataObj, function (res) {
            var jsonObj = JSON.parse(res);//testdata;
            const dates = jsonObj['data'].rankData;
            const pmsdatasnapshotdata = jsonObj['data']['pmsvalues'].snapshotdata;
            const pmsdatasnapoffsetdata = jsonObj['data']['pmsvalues'].offsetdata;
            const marketvaluesdata = jsonObj['data']['marketvalues'];
            let rows = [];
            const compSetAvgColorCoder = flag.makeCompSetAvgGradientColorCoder(marketvaluesdata);

            let wccnt = 0;
            dates.forEach(stayDate => {
                let rowCells = [];

                wccnt++;
                const prettyStayDate = window.infinito.vao.controller.dateHelper.getShortDayDateMonFullYearString(stayDate);

                rowCells.push(flag.acquireTableCell(stayDate+"_R" , {
                    field: cellFieldMap.stayDate,
                    value: html`
                        <vao-button
                                text="${prettyStayDate}"
                                variant="invert"
                                size="xs"
                                @click=${flag.onClickStayDate.bind(flag, stayDate)}
                        ></vao-button>
                    `
                }));

                if (
                    pmsdatasnapshotdata.roomsToSell
                    && pmsdatasnapshotdata.roomsToSell[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsToSell[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsToSell[stayDate].value)
                ) {
                    let value = parseInt(pmsdatasnapshotdata.roomsToSell[stayDate].value, 10);
                    let labelColor = null;
                    if (value !== 0) {
                        //value *= -1; // Because Fabian dont wants the inverse rendered.
                        if (value < 0) {
                            // There actually is rooms still left to sell.
                            labelColor = Constants.COLORS.HEX.DANGER;
                        }
                    }
                    rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                        field: cellFieldMap.roomsToSell,
                        value: html`
                            <vao-tag
                                    .label="${value}"
                                    .labelColor="${labelColor}"
                                    canInterpretBgColor="false"
                            ></vao-tag>
                        `
                    }));
                }

                if (
                    pmsdatasnapshotdata.roomsOtb
                    && pmsdatasnapshotdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsOtb[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                        field: cellFieldMap.roomsOtb,
                        value: pmsdatasnapshotdata.roomsOtb[stayDate].value
                    }));

                }

                if (
                    pmsdatasnapshotdata.roomsOtb
                    && pmsdatasnapshotdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsOtb[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                        field: cellFieldMap.forecast,
                        value: html`
                            <vao-story-forecast-subtitle
                                    .recordDate="${flag.recordDate}"
                                    .stayDate="${stayDate}"
                                    .hotel="${flag.hotel}"
                            ></vao-story-forecast-subtitle>
                        `
                    }));
                }
                if (
                    pmsdatasnapshotdata.roomsOtb
                    && pmsdatasnapshotdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsOtb[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                        field: cellFieldMap.fcrange,
                        value: html`
                            <vao-story-forecastrange-subtitle
                                    .recordDate="${flag.recordDate}"
                                    .stayDate="${stayDate}"
                                    .hotel="${flag.hotel}"
                            ></vao-story-forecastrange-subtitle>
                        `
                    }));
                }
                if (
                    pmsdatasnapshotdata.roomsOtb
                    && pmsdatasnapshotdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsOtb[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                        field: cellFieldMap.expectedPickup,
                        value: html`
                            <vao-story-what-to-expect-pickup-subtitle
                                    .recordDate="${flag.recordDate}"
                                    .stayDate="${stayDate}"
                                    .hotel="${flag.hotel}"
                            ></vao-story-what-to-expect-pickup-subtitle>
                        `
                    }));
                }

                if (
                    pmsdatasnapshotdata.occupancy
                    && pmsdatasnapshotdata.occupancy[stayDate]
                    && 'value' in pmsdatasnapshotdata.occupancy[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.occupancy[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                        field: cellFieldMap.occupancy,
                        value: flag.formatPercentageValue(pmsdatasnapshotdata.occupancy[stayDate].value)
                    }));
                }


                if (
                    pmsdatasnapshotdata.revenueNet
                    && pmsdatasnapshotdata.revenueNet[stayDate]
                    && 'value' in pmsdatasnapshotdata.revenueNet[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.revenueNet[stayDate].value)
                ) {
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        pmsdatasnapshotdata.revenueNet[stayDate].value,
                        Constants.REVENUE_DIGITS,
                        true,
                        flag.hotel.locale
                    );
                    rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                        field: cellFieldMap.revenue,
                        value: formatted
                    }));
                }

                if (
                    pmsdatasnapshotdata.adrNet
                    && pmsdatasnapshotdata.adrNet[stayDate]
                    && 'value' in pmsdatasnapshotdata.adrNet[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.adrNet[stayDate].value)
                ) {
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        pmsdatasnapshotdata.adrNet[stayDate].value,
                        Constants.ADR_DIGITS,
                        true,
                        flag.hotel.locale,
                        1
                    );

                    rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                        field: cellFieldMap.adr,
                        value: formatted
                    }));
                }

                if (
                    pmsdatasnapoffsetdata.roomsOtb
                    && pmsdatasnapoffsetdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapoffsetdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapoffsetdata.roomsOtb[stayDate].value)
                ) {
                    const value = pmsdatasnapoffsetdata.roomsOtb[stayDate].value;
                    const valueInt = parseInt(value, 10);
                    const color = window.infinito.vao.controller.pickupHelper.getPickupColorFor(valueInt);
                    let bgColor = null;
                    if (color) {
                        bgColor = ColorArray.hexToRGBA(color, .15);
                    }
                    const numVarBlock = html`
                        <vao-numeric-variance-block
                                numericValue="${value}"
                                formattedValue="${value}"
                        ></vao-numeric-variance-block>
                    `;
                    rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                        field: cellFieldMap.roomsOtbPickup,
                        value: html`
                            <vao-tag
                                    .label="${numVarBlock}"
                                    bgColor="${bgColor}"
                            ></vao-tag>
                        `
                    }));
                }

                if (
                    pmsdatasnapoffsetdata.revenueNet
                    && pmsdatasnapoffsetdata.revenueNet[stayDate]
                    && 'value' in pmsdatasnapoffsetdata.revenueNet[stayDate]
                    && $.isNumeric(pmsdatasnapoffsetdata.revenueNet[stayDate].value)
                ) {
                    const value = pmsdatasnapoffsetdata.revenueNet[stayDate].value;
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        value,
                        Constants.REVENUE_DIGITS,
                        true,
                        flag.hotel.locale
                    );
                    rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                        field: cellFieldMap.revenuePickup,
                        value: html`
                            <vao-numeric-variance-block
                                    numericValue="${value}"
                                    formattedValue="${formatted}"
                            ></vao-numeric-variance-block>
                        `
                    }));
                }

                if (
                    pmsdatasnapoffsetdata.adrNet
                    && pmsdatasnapoffsetdata.adrNet[stayDate]
                    && 'value' in pmsdatasnapoffsetdata.adrNet[stayDate]
                    && $.isNumeric(pmsdatasnapoffsetdata.adrNet[stayDate].value)
                ) {
                    const value = pmsdatasnapoffsetdata.adrNet[stayDate].value;
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        value,
                        Constants.ADR_DIGITS,
                        true,
                        flag.hotel.locale,
                        1
                    );
                    rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                        field: cellFieldMap.pickupsAdr,
                        value: html`
                            <vao-numeric-variance-block
                                    numericValue="${value}"
                                    formattedValue="${formatted}"
                            ></vao-numeric-variance-block>
                        `
                    }));
                }

                /******What Changed********/
                rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                    field: cellFieldMap.WhatChanged,
                    value: html`<div><span style="margin-right:5px;color: #03a9f3">Low</span> to <span style="margin-left:5px;color: #ffc107">Medium</span></div>`
                }));

                /********MY PACE**********/
                let mypace = flag.fetchmypace(stayDate+"_R",flag.recordDate,rowCells);
                rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                    field: cellFieldMap.roomsOtbPace,
                    value: ''
                }));

                /********MY PIRCE*********/
                let myprice = flag.fetchMyprice(stayDate+"_R",flag.recordDate,rowCells);
                rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                    field: cellFieldMap.ratePace,
                    value: '',
                }));

                /**********POSITION**********/
                let normality = flag.fetchPosition(stayDate+"_R",flag.recordDate,rowCells);
                rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                    field: cellFieldMap.positioning,
                    value: ''
                }));

                /**************RATE COLUMN************/
                if (
                    marketvaluesdata.rate
                    && marketvaluesdata.rate[stayDate]
                    && 'value' in marketvaluesdata.rate[stayDate]
                    /*&& $.isNumeric(marketvaluesdata.rate[stayDate].value)*/
                ) {
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        marketvaluesdata.rate[stayDate].value,
                        Constants.RATE_DIGITS,
                        true,
                        flag.hotel.locale
                    );
                    rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                        field: cellFieldMap.rate,
                        value: formatted
                    }));
                }
                /**************COMP AVG COLUMN************/
                if (
                    marketvaluesdata.compAverageRate
                    && marketvaluesdata.compAverageRate[stayDate]
                    && 'value' in marketvaluesdata.compAverageRate[stayDate]
                    && $.isNumeric(marketvaluesdata.compAverageRate[stayDate].value)
                ) {
                    const diff = marketvaluesdata.compAverageRate[stayDate].value;
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        diff,
                        Constants.RATE_DIGITS,
                        true,
                        flag.hotel.locale
                    );
                    const bgColor = compSetAvgColorCoder(diff);
                    /*bgColor="${bgColor}"*/
                    rowCells.push(flag.acquireTableCell(stayDate+"_R", {
                            field: cellFieldMap.compSetAvg,
                            value: html`<vao-tag label="${formatted}"></vao-tag>`
                        }
                    ));
                }

                rows.push(new TableRow(rowCells));
                flag.rmsOTBdata.push(new TableRow(rowCells));
            });

            flag.mergeData('rmsOTB',flag.rmsOTBdata);

        });

    }

    mergeData(call,data){
        //this.isLoading = true;
        if(call == 'pace'){
            this.paceLoaded = true;
            document.getElementById("pacemvmt").style.backgroundColor = "#4caf50";
            document.getElementById("pacemvmt").style.borderColor = "#4caf50";
        }else if(call == 'forecast'){
            this.forecastLoaded = true;
            document.getElementById("forecastmvmt").style.backgroundColor = "#4caf50";
            document.getElementById("forecastmvmt").style.borderColor = "#4caf50";
        }else if(call == 'position'){
            this.positionLoaded = true;
        }else if(call == 'rmsOTB'){
            this.rmsOTBLoaded = true;
            document.getElementById("occmvnt").style.backgroundColor = "#4caf50";
            document.getElementById("occmvnt").style.borderColor = "#4caf50";
        }


        /********PULL OUT THE PRESENT ROWS TO RE-POPULATE THE TABLE*********/
        let dt = [];
        if(this.rows.length > 0){
            dt = this.rows;
            this.rows = [];
            this.rowData = [];
        }
        /**********ADD ROWS DATA TO TABLE***********/
        for(var i=0;i<dt.length;i++){
            this.rowData.push(dt[i]);
        }
        for(var j=0;j<data.length;j++){
            this.rowData.push(data[j]);
        }

        this.rowData.sort(function(a,b){
            var att = a['tableCellPerField'];
            var at1 = att.entries();
            var astayDate = at1.next().value[1]['__value']['values'][0];

            var btt = b['tableCellPerField'];
            var bt1 = btt.entries();
            var bstayDate = bt1.next().value[1]['__value']['values'][0];
            return new Date(astayDate) - new Date(bstayDate)
        })

        this.rows = this.rowData;
        this.isLoading = false;
        // if(data.length > 0){
        //
        //
        // }


    }

    SendForecastRequest(){
        document.getElementById("forecastmvmt").style.backgroundColor = "#ffae01";
        document.getElementById("forecastmvmt").style.borderColor = "#ffae01";
        let dataObj = {
            'hotel_id': this.hotel.id,
            'recordDate': infinito.vao.controller.storageHelper.getCurrentDate(),
            'stayDate': infinito.vao.controller.storageHelper.getCurrentDate(),
            'offset': infinito.vao.controller.storageHelper.getPickupOffset(),
            'op': 'getForecastruledata',
            'serviceName': "ivirulesapi.php"
        };
        let flag = this;
        // FORECAST DATA
        sendRequest(dataObj, function (res){

            let jsonObj = JSON.parse(res);
            const dates = jsonObj['data'].rankData;
            const pmsdatasnapshotdata = jsonObj['data']['pmsvalues'].snapshotdata;
            const pmsdatasnapoffsetdata = jsonObj['data']['pmsvalues'].offsetdata;

            const marketvaluesdata = jsonObj['data']['marketvalues'];
            let rows = [];
            const compSetAvgColorCoder = flag.makeCompSetAvgGradientColorCoder(marketvaluesdata);

            dates.forEach(stayDate => {

                let rowCells = [];

                const prettyStayDate = window.infinito.vao.controller.dateHelper.getShortDayDateMonFullYearString(stayDate);

                rowCells.push(flag.acquireTableCell(stayDate+"_F" , {
                    field: cellFieldMap.stayDate,
                    value: html`
                        <vao-button
                                text="${prettyStayDate}"
                                variant="invert"
                                size="xs"
                                @click=${flag.onClickStayDate.bind(flag, stayDate)}
                        ></vao-button>
                    `
                }));

                if (
                    pmsdatasnapshotdata.roomsToSell
                    && pmsdatasnapshotdata.roomsToSell[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsToSell[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsToSell[stayDate].value)
                ) {
                    let value = parseInt(pmsdatasnapshotdata.roomsToSell[stayDate].value, 10);
                    let labelColor = null;
                    if (value !== 0) {
                        //value *= -1; // Because Fabian dont wants the inverse rendered.
                        if (value < 0) {
                            // There actually is rooms still left to sell.
                            labelColor = Constants.COLORS.HEX.DANGER;
                        }
                    }
                    rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.roomsToSell,
                        value: html`
                            <vao-tag
                                    .label="${value}"
                                    .labelColor="${labelColor}"
                                    canInterpretBgColor="false"
                            ></vao-tag>
                        `
                    }));
                }

                if (
                    pmsdatasnapshotdata.roomsOtb
                    && pmsdatasnapshotdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsOtb[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.roomsOtb,
                        value: pmsdatasnapshotdata.roomsOtb[stayDate].value
                    }));

                }

                if (
                    pmsdatasnapshotdata.roomsOtb
                    && pmsdatasnapshotdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsOtb[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.forecast,
                        value: html`
                            <vao-story-forecast-subtitle
                                    .recordDate="${flag.recordDate}"
                                    .stayDate="${stayDate}"
                                    .hotel="${flag.hotel}"
                            ></vao-story-forecast-subtitle>
                        `
                    }));
                }
                if (
                    pmsdatasnapshotdata.roomsOtb
                    && pmsdatasnapshotdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsOtb[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.fcrange,
                        value: html`
                            <vao-story-forecastrange-subtitle
                                    .recordDate="${flag.recordDate}"
                                    .stayDate="${stayDate}"
                                    .hotel="${flag.hotel}"
                            ></vao-story-forecastrange-subtitle>
                        `
                    }));
                }
                if (
                    pmsdatasnapshotdata.roomsOtb
                    && pmsdatasnapshotdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapshotdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.roomsOtb[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.expectedPickup,
                        value: html`
                            <vao-story-what-to-expect-pickup-subtitle
                                    .recordDate="${flag.recordDate}"
                                    .stayDate="${stayDate}"
                                    .hotel="${flag.hotel}"
                            ></vao-story-what-to-expect-pickup-subtitle>
                        `
                    }));
                }

                if (
                    pmsdatasnapshotdata.occupancy
                    && pmsdatasnapshotdata.occupancy[stayDate]
                    && 'value' in pmsdatasnapshotdata.occupancy[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.occupancy[stayDate].value)
                ) {
                    rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.occupancy,
                        value: flag.formatPercentageValue(pmsdatasnapshotdata.occupancy[stayDate].value)
                    }));
                }


                if (
                    pmsdatasnapshotdata.revenueNet
                    && pmsdatasnapshotdata.revenueNet[stayDate]
                    && 'value' in pmsdatasnapshotdata.revenueNet[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.revenueNet[stayDate].value)
                ) {
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        pmsdatasnapshotdata.revenueNet[stayDate].value,
                        Constants.REVENUE_DIGITS,
                        true,
                        flag.hotel.locale
                    );
                    rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.revenue,
                        value: formatted
                    }));
                }

                if (
                    pmsdatasnapshotdata.adrNet
                    && pmsdatasnapshotdata.adrNet[stayDate]
                    && 'value' in pmsdatasnapshotdata.adrNet[stayDate]
                    && $.isNumeric(pmsdatasnapshotdata.adrNet[stayDate].value)
                ) {
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        pmsdatasnapshotdata.adrNet[stayDate].value,
                        Constants.ADR_DIGITS,
                        true,
                        flag.hotel.locale,
                        1
                    );

                    rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.adr,
                        value: formatted
                    }));
                }

                if (
                    pmsdatasnapoffsetdata.roomsOtb
                    && pmsdatasnapoffsetdata.roomsOtb[stayDate]
                    && 'value' in pmsdatasnapoffsetdata.roomsOtb[stayDate]
                    && $.isNumeric(pmsdatasnapoffsetdata.roomsOtb[stayDate].value)
                ) {
                    const value = pmsdatasnapoffsetdata.roomsOtb[stayDate].value;
                    const valueInt = parseInt(value, 10);
                    const color = window.infinito.vao.controller.pickupHelper.getPickupColorFor(valueInt);
                    let bgColor = null;
                    if (color) {
                        bgColor = ColorArray.hexToRGBA(color, .15);
                    }
                    const numVarBlock = html`
                        <vao-numeric-variance-block
                                numericValue="${value}"
                                formattedValue="${value}"
                        ></vao-numeric-variance-block>
                    `;
                    rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.roomsOtbPickup,
                        value: html`
                            <vao-tag
                                    .label="${numVarBlock}"
                                    bgColor="${bgColor}"
                            ></vao-tag>
                        `
                    }));
                }

                if (
                    pmsdatasnapoffsetdata.revenueNet
                    && pmsdatasnapoffsetdata.revenueNet[stayDate]
                    && 'value' in pmsdatasnapoffsetdata.revenueNet[stayDate]
                    && $.isNumeric(pmsdatasnapoffsetdata.revenueNet[stayDate].value)
                ) {
                    const value = pmsdatasnapoffsetdata.revenueNet[stayDate].value;
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        value,
                        Constants.REVENUE_DIGITS,
                        true,
                        flag.hotel.locale
                    );
                    rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.revenuePickup,
                        value: html`
                            <vao-numeric-variance-block
                                    numericValue="${value}"
                                    formattedValue="${formatted}"
                            ></vao-numeric-variance-block>
                        `
                    }));
                }

                if (
                    pmsdatasnapoffsetdata.adrNet
                    && pmsdatasnapoffsetdata.adrNet[stayDate]
                    && 'value' in pmsdatasnapoffsetdata.adrNet[stayDate]
                    && $.isNumeric(pmsdatasnapoffsetdata.adrNet[stayDate].value)
                ) {
                    const value = pmsdatasnapoffsetdata.adrNet[stayDate].value;
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        value,
                        Constants.ADR_DIGITS,
                        true,
                        flag.hotel.locale,
                        1
                    );
                    rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.pickupsAdr,
                        value: html`
                            <vao-numeric-variance-block
                                    numericValue="${value}"
                                    formattedValue="${formatted}"
                            ></vao-numeric-variance-block>
                        `
                    }));
                }


                /******What Changed********/
                var date = new Date(flag.recordDate);
                date.setDate(date.getDate() - 1);
                var date = date.toJSON().slice(0, 10);
                let yesterdaydt = date.slice(0, 4)+'-'+date.slice(5, 7) + '-'+date.slice(8, 10);
                let fr = flag.getForecastData(yesterdaydt,flag.recordDate,stayDate,flag.hotel.id,rowCells);
                rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                    field: cellFieldMap.WhatChanged,
                    value: ''
                }));

                /********MY PACE**********/
                let mypace = flag.fetchmypace(stayDate+"_F",flag.recordDate,rowCells);
                rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                    field: cellFieldMap.roomsOtbPace,
                    value: ''
                }));

                /********MY PIRCE*********/
                let myprice = flag.fetchMyprice(stayDate+"_F",flag.recordDate,rowCells);
                rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                    field: cellFieldMap.ratePace,
                    value: '',
                }));


                let normality = flag.fetchPosition(stayDate+"_F",flag.recordDate,rowCells);
                rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                    field: cellFieldMap.positioning,
                    value: ''
                }));

                /**************RATE COLUMN************/
                if (
                    marketvaluesdata.rate
                    && marketvaluesdata.rate[stayDate]
                    && 'value' in marketvaluesdata.rate[stayDate]
                    /*&& $.isNumeric(marketvaluesdata.rate[stayDate].value)*/
                ) {
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        marketvaluesdata.rate[stayDate].value,
                        Constants.RATE_DIGITS,
                        true,
                        flag.hotel.locale
                    );

                    rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.rate,
                        value: formatted
                    }));
                }
                /**************COMP AVG COLUMN************/
                if (
                    marketvaluesdata.compAverageRate
                    && marketvaluesdata.compAverageRate[stayDate]
                    && 'value' in marketvaluesdata.compAverageRate[stayDate]
                    && $.isNumeric(marketvaluesdata.compAverageRate[stayDate].value)
                ) {
                    const diff = marketvaluesdata.compAverageRate[stayDate].value;
                    const formatted = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        diff,
                        Constants.RATE_DIGITS,
                        true,
                        flag.hotel.locale
                    );
                    const bgColor = compSetAvgColorCoder(diff);
                    rowCells.push(flag.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.compSetAvg,
                        value: html`
                            <vao-tag
                                    label="${formatted}"
                            ></vao-tag>
                        `
                    }));

                }

                flag.forecastdata.push(new TableRow(rowCells));

            });
            flag.mergeData('forecast',flag.forecastdata);
        });

    }

    getForecastData(yesterdaydt,recordDate,stayDate,hotel,rowCells){
        let forecast1 = '';
        let bgcolor1 = '';
        let fcrangetext = '';
        let fcrangevalue = '';

        let modelBiStats = window.infinito.vao.model.biStatistics;
        modelBiStats.fetchStatistics(
            hotel,
            modelBiStats.buildQuery({
                recordDate: yesterdaydt,
                firstStayDate: stayDate,
                lastStayDate: stayDate,
                fields: [
                    modelBiStats.fields.occupancyLeaderCloserTrendProphecy
                ]
            }),
            (data, procData) => {
                const _procData = procData || {};
                let occProphecy =
                    ((_procData.occupancyLeaderCloserTrendProphecy || {})[stayDate] || {}).value || null;

                if (
                    typeof occProphecy === 'object'
                    && 'leadersClosersProphecy' in occProphecy
                    && $.isNumeric(occProphecy.leadersClosersProphecy)
                ) {
                    forecast1 = (window.infinito.vao.controller.utils.interpretOccupancyForecastOutput(
                        occProphecy.leadersClosersProphecy
                    ) || Constants.MISSING_STR);
                } else {
                    forecast1 = Constants.MISSING_STR;
                }
                if (forecast1 === 'no fill') {
                    bgcolor1 = 'color: #fff;background-color: #dc3545;';
                } else if (forecast1 === 'ability to fill') {
                    bgcolor1 = 'color: #212529;background-color: #ffc107;';
                } else if (forecast1 === 'excess demand') {
                    bgcolor1 = 'color: #fff;background-color: #28a745;';
                } else {
                    bgcolor1 = '';
                }

                var fForecast = parseFloat((occProphecy.leadersClosersProphecy || Constants.MISSING_STR));
                if (fForecast < 0.29) {
                    fcrangetext = '20s';
                    fcrangevalue = 20;
                } else if (fForecast > 0.29 && fForecast < 0.39) {
                    //this.forecast = '30-39%';
                    fcrangetext = '30s';
                    fcrangevalue = 30;
                } else if (fForecast >= 0.39 && fForecast < 0.49) {
                    //this.forecast = '40-49%';
                    fcrangetext = '40s';
                    fcrangevalue = 40;
                } else if (fForecast >= 0.49 && fForecast < 0.59) {
                    fcrangetext = '50s';
                    fcrangevalue = 50;
                } else if (fForecast >= 0.59 && fForecast < 0.69) {
                    fcrangetext = '60s';
                    fcrangevalue = 60;
                } else if (fForecast >= 0.69 && fForecast < 0.79) {
                    fcrangetext = '70s';
                    fcrangevalue = 70;
                } else if (fForecast >= 0.79 && fForecast < 0.89) {
                    fcrangetext = '80s';
                    fcrangevalue = 80;
                } else if (fForecast >= 0.89 && fForecast < 0.95) {
                    fcrangetext = '90';
                    fcrangevalue = 90;
                } else if (fForecast > 0.95 && fForecast <= 1) {
                    //this.forecast = '95-100%';
                    fcrangetext = '95+';
                    fcrangevalue = 95;
                } else {
                    fcrangetext = '101+';
                    fcrangevalue = 101;
                }

                this.getForecastTwo(recordDate,stayDate,hotel,rowCells,forecast1,bgcolor1,fcrangetext,fcrangevalue);
            });

    }

    getForecastTwo(recordDate,stayDate,hotel,rowCells,forecast1,bgcolor1,fcrangetext,fcrangevalue){
        let forecast2 = '';
        let bgcolor2 = '';
        let fcrangetext2 = '';
        let fcrangevalue2 = '';
        let modelBiStats = window.infinito.vao.model.biStatistics;
        modelBiStats.fetchStatistics(
            hotel,
            modelBiStats.buildQuery({
                recordDate: recordDate,
                firstStayDate: stayDate,
                lastStayDate: stayDate,
                fields: [
                    modelBiStats.fields.occupancyLeaderCloserTrendProphecy
                ]
            }),
            (data, procData) => {
                const _procData = procData || {};
                let occProphecy =
                    ((_procData.occupancyLeaderCloserTrendProphecy || {})[stayDate] || {}).value || null;

                if (
                    typeof occProphecy === 'object'
                    && 'leadersClosersProphecy' in occProphecy
                    && $.isNumeric(occProphecy.leadersClosersProphecy)
                ) {
                    forecast2 = (window.infinito.vao.controller.utils.interpretOccupancyForecastOutput(
                        occProphecy.leadersClosersProphecy
                    ) || Constants.MISSING_STR);
                } else {
                    forecast2 = Constants.MISSING_STR;
                }

                if (forecast2 === 'no fill') {
                    bgcolor2 = 'color: #fff;background-color: #dc3545;';
                } else if (forecast2 === 'ability to fill') {
                    bgcolor2 = 'color: #212529;background-color: #ffc107;';
                } else if (forecast2 === 'excess demand') {
                    bgcolor2 = 'color: #fff;background-color: #28a745;';
                } else {
                    bgcolor2 = '';
                }
                var fForecast = parseFloat((occProphecy.leadersClosersProphecy || Constants.MISSING_STR));

                if (fForecast < 0.29) {
                    fcrangetext2 = '20s';
                    fcrangevalue2 = 20;
                } else if (fForecast > 0.29 && fForecast < 0.39) {
                    fcrangetext2 = '30s';
                    fcrangevalue2 = 30;
                } else if (fForecast >= 0.39 && fForecast < 0.49) {
                    fcrangetext2 = '40s';
                    fcrangevalue2 = 40;
                } else if (fForecast >= 0.49 && fForecast < 0.59) {
                    fcrangetext2 = '50s';
                    fcrangevalue2 = 50;
                } else if (fForecast >= 0.59 && fForecast < 0.69) {
                    fcrangetext2 = '60s';
                    fcrangevalue2 = 60;
                } else if (fForecast >= 0.69 && fForecast < 0.79) {
                    fcrangetext2 = '70s';
                    fcrangevalue2 = 70;
                } else if (fForecast >= 0.79 && fForecast < 0.89) {
                    fcrangetext2 = '80s';
                    fcrangevalue2 = 80;
                } else if (fForecast >= 0.89 && fForecast < 0.95) {
                    fcrangetext2 = '90+';
                    fcrangevalue2 = 90;
                } else if (fForecast > 0.95 && fForecast <= 1) {
                    //this.forecast = '95-100%';
                    fcrangetext2 = '95+';
                    fcrangevalue2 = 95;
                } else {
                    fcrangetext2 = '101+';
                    fcrangevalue2 = 101;
                }

                //if(forecast1 == forecast2 || forecast1 == '' || forecast2 == '' || forecast1 == '-' || forecast2 == '-'){
                if(fcrangevalue == fcrangevalue2 || fcrangevalue == '' || fcrangevalue2 == '' || fcrangevalue == '-' || fcrangevalue2 == '-'){
                    let index = -1;
                    let istayDate = window.infinito.vao.controller.dateHelper.getShortDayDateMonFullYearString(stayDate);
                    for(let j=0;j<this.rows.length;j++) {
                        var jtt = this.rows[j]['tableCellPerField'];
                        var jt1 = jtt.entries();
                        var jstayDate = jt1.next().value[1]['__value']['values'][0];
                        if (jstayDate == istayDate) {
                            //find what changed value.
                            var dt1 = this.rows[j]['tableCellPerField'];
                            var dt2 = dt1.entries();
                            for (let e of dt2) {
                                if(e[0] == "What Changed"){
                                    if(e[1]['__value'] == '-'){
                                        index=j;
                                        this.rows.splice(j,1);
                                        this.repopulate();
                                        break;
                                    }
                                }
                            }
                        }
                    }

                }else{
                    /*****CHECK FORECAST AND FORECAST2 RANGE*****/
                    let backgroundis = '';
                    if(fcrangevalue2 > fcrangevalue){
                        backgroundis = 'color: #fff;background-color: #28a745;';
                    }else{
                        backgroundis = 'color: #fff;background-color: #dc3545;';
                    }

                    rowCells.push(this.acquireTableCell(stayDate+"_F", {
                        field: cellFieldMap.WhatChanged,
                        value: html`<div><span style="${backgroundis}padding: 2px 3px;border-radius: 6px;font-size: 10.2px;">${fcrangetext} To ${fcrangetext2}</span></div>`
                    }));
                    //html`<div><span style="${backgroundis}padding: 2px 3px;border-radius: 6px;font-size: 10.2px;">${fcrangetext} To ${fcrangetext2}</span></div>`
                    //html`<div><span style="${bgcolor1}padding: 0px 5px 0px 5px;border-radius: 6px;margin-right: 5px;font-size: 12px;">${forecast1}</span> To <span style="${bgcolor2}padding: 0px 5px 0px 5px;border-radius: 6px;margin-left: 5px;font-size: 12px;">${forecast2}</span></div>`
                }

            });
    }

    fetchmypace(stayDate,recordDate,rowCells){
        let calltype = stayDate.substring(12,10);
        stayDate = stayDate.substring(0,10);
        let modelBiStats = window.infinito.vao.model.biStatistics;
        modelBiStats.fetchStatistics(
            this.hotel.id,
            modelBiStats.buildQuery({
                recordDate: recordDate,
                firstStayDate: stayDate,
                lastStayDate: stayDate,
                fields: [
                    modelBiStats.fields.roomsOtb,
                    modelBiStats.fields.roomsOtbLeadMedian,
                ],
                evolutionRange: 14
            }),
            (data, procData) => {
                let otbVal = ((procData.roomsOtb || {})[stayDate] || {}).value || null;
                let otbLeadMedVal = ((procData.roomsOtbLeadMedian || {})[stayDate] || {}).value || null;

                // Put these here to help batch lit async DOM update
                this.dow = window.infinito.vao.controller.dateHelper.getFullDayString(stayDate);
                this.leadTime = window.infinito.vao.controller.dateHelper.calcDaysBetweenDates(
                    this.stayDate,
                    this.recordDate
                );

                if (
                    $.isNumeric(otbVal)
                    && typeof otbLeadMedVal === 'object'
                    && 'median' in otbLeadMedVal
                    && $.isNumeric(otbLeadMedVal.median)
                ) {
                    let diff = otbVal - otbLeadMedVal.median;
                    this.rmsPaceVar = Math.abs(diff);
                    if (diff < 0) {
                        this.rmsPaceDesc = 'behind';
                    } else if (diff > 0) {
                        this.rmsPaceDesc = 'ahead';
                    } else {
                        this.rmsPaceDesc = 'inline';
                    }
                } else {
                    this.rmsPaceDesc = Constants.MISSING_STR;
                    this.rmsPaceVar = Constants.MISSING_STR;
                }

                rowCells.push(this.acquireTableCell(stayDate+calltype, {
                    field: cellFieldMap.roomsOtbPace,
                    value: html`
                        <div class="vao__components--storyInternalKpiPace">
                            ${storyHtml`<p style="margin: auto;">${new StoryText(this.rmsPaceDesc, true, storyTextTypes.TYPE_TEXTUAL)} of pace
                    by ${new StoryText(this.rmsPaceVar, true, storyTextTypes.TYPE_NEUTRAL)} rooms</p>`} </div>`
                }));

            })
    }

    fetchPaceWhatChanged(stayDate,rowCells,i){
        let calltype = stayDate.substring(12,10);
        stayDate = stayDate.substring(0,10);
        let modelBiStats = window.infinito.vao.model.biStatistics;
        modelBiStats.fetchStatistics(
            this.hotel.id,
            modelBiStats.buildQuery({
                recordDate: infinito.vao.controller.storageHelper.getCurrentDate(),
                firstStayDate: stayDate,
                lastStayDate: stayDate,
                fields: [
                    modelBiStats.fields.revenueLeadSiblingPace,
                    modelBiStats.fields.roomsOtbLeadSiblingPaceEvolution
                ],
                evolutionRange: 14
            }),
            (data, procData) => {
                let revLeadSibPaceVal = ((procData.revenueLeadSiblingPace || {})[stayDate] || {}).value || null;
                let otbLeadSibPaceEvoVal = ((procData.roomsOtbLeadSiblingPaceEvolution || {})[stayDate] || {}).value || null;

                if (
                    typeof revLeadSibPaceVal === 'object'
                    && 'T0' in revLeadSibPaceVal
                    && 'setDenseRank' in revLeadSibPaceVal.T0
                    && 'setDenseLength' in revLeadSibPaceVal.T0
                ) {
                    this.paceDesc = window.infinito.vao.controller.analyseAttributesHelper.scaleRankMapForUniqueLength(
                        paceDescMap,
                        revLeadSibPaceVal.T0.setDenseLength
                    )[revLeadSibPaceVal.T0.setDenseRank] || Constants.MISSING_STR;
                } else {
                    this.paceDesc = Constants.MISSING_STR;
                }


                if (typeof otbLeadSibPaceEvoVal === 'object') {
                    let sorted = Object.values(otbLeadSibPaceEvoVal).sort((a, b) => {
                        return a.leadTime - b.leadTime;
                    });
                    let now = sorted[0];
                    if (
                        typeof now.value === 'object'
                        && 'T0' in now.value
                        && 'setDenseRank' in now.value.T0
                        && 'setDenseLength' in now.value.T0
                        && $.isNumeric(now.value.T0.setDenseRank)
                    ) {
                        let nowRank = now.value.T0.setDenseRank;
                        let nowLength = now.value.T0.setDenseLength;
                        let thenRank = null;
                        let thenLength = null;
                        let i;
                        for (i = 1; i <= 14; i++) {
                            let then = sorted[i] || null;
                            if (
                                typeof then === 'object'
                                && 'value' in then
                                && 'T0' in then.value
                                && 'setDenseRank' in then.value.T0
                                && 'setDenseLength' in then.value.T0
                                && $.isNumeric(then.value.T0.setDenseRank)
                            ) {
                                thenRank = then.value.T0.setDenseRank;
                                thenLength = then.value.T0.setDenseLength;
                            }
                            if (thenRank !== null && thenRank !== nowRank) {
                                break;
                            }
                        }
                        this.paceDiffInterval = i;
                        if (nowRank !== null) {
                            this.paceTo = window.infinito.vao.controller.analyseAttributesHelper
                                .scaleRankMapForUniqueLength(
                                    paceDescMap,
                                    nowLength
                                )[nowRank] || Constants.MISSING_STR;
                        } else {
                            this.paceTo = Constants.MISSING_STR;
                        }
                        if (thenRank !== null) {
                            this.paceFrom = window.infinito.vao.controller.analyseAttributesHelper
                                .scaleRankMapForUniqueLength(
                                    paceDescMap,
                                    thenLength
                                )[thenRank] || Constants.MISSING_STR;
                        } else {
                            this.paceFrom = Constants.MISSING_STR;
                        }
                        if (nowRank && thenRank && nowRank !== thenRank) {
                            if (nowRank < thenRank) {
                                this.paceDiff = 'Pace improved';
                            } else if (nowRank > thenRank) {
                                this.paceDiff = 'Pace fallen behind';
                            } else {
                                this.paceDiff = 'Pace not changed';
                            }
                        } else {
                            this.paceDiff = Constants.MISSING_STR;
                        }
                    } else {
                        this.paceDiff = Constants.MISSING_STR;
                        this.paceDiffInterval = Constants.MISSING_STR;
                        this.paceFrom = Constants.MISSING_STR;
                        this.paceTo = Constants.MISSING_STR;
                    }
                } else {
                    this.paceDiff = Constants.MISSING_STR;
                    this.paceDiffInterval = Constants.MISSING_STR;
                    this.paceFrom = Constants.MISSING_STR;
                    this.paceTo = Constants.MISSING_STR;
                }
                if(this.paceDiff == '-'){

                    var istayDate = window.infinito.vao.controller.dateHelper.getShortDayDateMonFullYearString(stayDate);

                    let index=-1;
                    for(let j=0;j<this.rows.length;j++) {
                        var jtt = this.rows[j]['tableCellPerField'];
                        var jt1 = jtt.entries();
                        var jstayDate = jt1.next().value[1]['__value']['values'][0];
                        if (jstayDate == istayDate) {
                            //find what changed value.
                            var dt1 = this.rows[j]['tableCellPerField'];
                            var dt2 = dt1.entries();
                            for (let e of dt2) {
                                if(e[0] == "What Changed"){
                                    if(e[1]['__value'] == '-'){
                                        index=j;
                                        this.rows.splice(j,1);
                                        this.repopulate();
                                        break;
                                    }
                                }
                            }
                        }
                    }
                    this.repopulate();
                }else{

                    rowCells.push(this.acquireTableCell(stayDate+calltype, {
                        field: cellFieldMap.WhatChanged,
                        value: storyHtml `${new StoryText(this.paceDiff, true, storyTextTypes.TYPE_TEXTUAL)}`
                    }));
                }
            });

    }
    repopulate(){
        //this.loading = true;
        var dt = this.rows;
        this.rows = [];
        for(var i=0;i<dt.length;i++){
            this.rows.push(dt[i]);
        }
        this.loading = false;
    }

    fetchPosition(stayDate,recordDate,rowCells){
        let calltype = stayDate.substring(12,10);
        stayDate = stayDate.substring(0,10);
        let normality = Constants.MISSING_STR;
        const biStatistics = window.infinito.vao.model.biStatistics;
        biStatistics.fetchStatistics(
            this.hotel.id,
            biStatistics.buildQuery({
                recordDate: recordDate,
                firstStayDate: stayDate,
                lastStayDate: stayDate,
                fields: [
                    biStatistics.fields.priceRank,
                    biStatistics.fields.priceRankLeadSiblingPace,
                ],
                pickupOffset: this.pickupSetting
            }),
            (data, procData) => {

                let typicalPriceRank = '';
                let _procData = procData;
                let priceRankLeadSiblingPace =
                    ((_procData.priceRankLeadSiblingPace || {})[stayDate] || {}).value || null;

                if (
                    priceRankLeadSiblingPace
                    && typeof priceRankLeadSiblingPace === 'object'
                    && 'T0' in priceRankLeadSiblingPace
                    && 'setRank' in priceRankLeadSiblingPace.T0
                    && $.isNumeric(priceRankLeadSiblingPace.T0.setRank)
                ) {
                    let setLength = parseInt(priceRankLeadSiblingPace.T0.setLength);
                    let setRankSum = 0;
                    setRankSum += (parseInt(priceRankLeadSiblingPace.T0.value || 0) || 0);
                    setRankSum += (parseInt(priceRankLeadSiblingPace.T1.value || 0) || 0);
                    setRankSum += (parseInt(priceRankLeadSiblingPace.T2.value || 0) || 0);
                    setRankSum += (parseInt(priceRankLeadSiblingPace.T3.value || 0) || 0);
                    setRankSum += (parseInt(priceRankLeadSiblingPace.T4.value || 0) || 0);
                    if (setLength) {
                        let avg = +parseFloat(setRankSum / setLength).toFixed(0); // + to drop 0's
                        typicalPriceRank = avg + '';
                    } else {
                        typicalPriceRank = Constants.MISSING_STR;
                    }
                } else {
                    typicalPriceRank = Constants.MISSING_STR;
                }
                let rateRank = _procData.priceRank[stayDate].value;

                if ($.isNumeric(rateRank) && $.isNumeric(typicalPriceRank)) {
                    const diff = parseInt(rateRank) - parseInt(typicalPriceRank);

                    if (diff <= 1 && diff >= -1) {
                        normality = new StoryText('Typical', true, storyTextTypes.TYPE_NEUTRAL);
                    } else if (diff > 0) {
                        normality = new StoryText('High', true, storyTextTypes.TYPE_POSITIVE);
                    } else {
                        normality = new StoryText('Low', true, storyTextTypes.TYPE_NEGATIVE);
                    }
                }

                rowCells.push(this.acquireTableCell(stayDate+calltype, {
                    field: cellFieldMap.positioning,
                    value: storyHtml`${normality}`,
                }));
            });

    }

    fetchMyprice(stayDate,recordDate,rowCells){
        let calltype = stayDate.substring(12,10);
        stayDate = stayDate.substring(0,10);
        let modelBiStats = window.infinito.vao.model.biStatistics;
        modelBiStats.fetchStatistics(
            this.hotel.id,
            modelBiStats.buildQuery({
                recordDate: recordDate,
                firstStayDate: stayDate,
                lastStayDate: stayDate,
                fields: [
                    modelBiStats.fields.rate,
                    modelBiStats.fields.rateLeadSiblingPace,
                    modelBiStats.fields.ratePickup
                ],
                pickupOffset: this.pickupSetting
            }),
            (data, procData) => {

                let rate = ((procData.rate || {})[stayDate] || {}).value || null;
                let ratePace = ((procData.rateLeadSiblingPace || {})[stayDate] || {}).value || null;
                let ratePU = ((procData.ratePickup || {})[stayDate] || {}).value || null;

                if ($.isNumeric(rate)) {
                    this.myRate = window.infinito.vao.controller.moneyHelper.formatMoneyBracketStyle(
                        rate,
                        Constants.RATE_DIGITS,
                        true,
                        this.locale
                    );
                } else {
                    this.myRate = Constants.MISSING_STR;
                }

                if (
                    typeof ratePace === 'object'
                    && 'T0' in ratePace
                    && typeof ratePace.T0 === 'object'
                    && 'setDenseRank' in ratePace.T0
                    && 'setDenseLength' in ratePace.T0
                ) {
                    let pnn = window.infinito.vao.controller.analyseAttributesHelper
                        .getMyPricePNNForSetRank(
                            ratePace.T0.setDenseRank,
                            ratePace.T0.setDenseLength
                        );
                    if (pnn) {
                        let prettyPnn = window.infinito.vao.controller.analyseAttributesHelper
                            .getMyPriceUserFriendlyForPNN(pnn);

                        if (prettyPnn) {
                            this.myRateConsider = prettyPnn;
                        } else {
                            this.myRateConsider = pnn;
                        }
                    } else {
                        this.myRateConsider = Constants.MISSING_STR;
                    }

                    let t0 = parseFloat((ratePace.T0 || {}).value || 0);
                    let t1 = parseFloat((ratePace.T1 || {}).value || 0);
                    let t2 = parseFloat((ratePace.T2 || {}).value || 0);
                    let t3 = parseFloat((ratePace.T3 || {}).value || 0);
                    let t4 = parseFloat((ratePace.T4 || {}).value || 0);
                    let set = [t0, t1, t2, t3, t4];
                    set = set.filter((setItem) => {
                        if (!setItem || typeof setItem !== 'number' || Number.isNaN(setItem) || setItem <= 0) {
                            return false;
                        }
                        return true;
                    });
                    let max = Math.max.apply(null, set);
                    let min = Math.min.apply(null, set);
                    this.typicalLow = min;
                    this.typicalHigh = max;
                    this.typicalNow = t0;
                } else {
                    this.myRateConsider = Constants.MISSING_STR;
                    this.typicalLow = Constants.MISSING_STR;
                    this.typicalHigh = Constants.MISSING_STR;
                    this.typicalNow = Constants.MISSING_STR;
                }

                rowCells.push(this.acquireTableCell(stayDate+calltype, {
                    field: cellFieldMap.ratePace,
                    value: this.myRateConsider=='-'
                        ? html`<p style="margin: auto;background: black;color: white;width: fit-content;padding: 0px 5px;border-radius: 5px;">Not Selling</p>`
                        : html`<div class="vao__components--storyMyPricing">
                                ${storyHtml`<p style="margin: auto;">Considered ${new StoryText(this.myRateConsider, true, storyTextTypes.TYPE_TEXTUAL)}</p>`}
                            </div>`,
                }));
            }
        );
    }
    updated(_changedProperties) {
        super.updated(_changedProperties);
        if (
            _changedProperties.has('hotel')
            || _changedProperties.has('recordDate')
            || _changedProperties.has('pickupSetting')
            || _changedProperties.has('startDate')
            || _changedProperties.has('endDate')
        ) {
            this.fill();
        }
    }

    onChangeTableOpts(e) {
        if (e.detail.newTableOpts) {
            this.tableOpts = e.detail.newTableOpts; // Share obj with table to not cause re-render.
        }
    }

    render() {
        return html`
            <div class="vao__components--pageDesktopSingleTable">
                <vao-table
                        stateKey="${this.tableStateKey}"
                        .tableOpts="${this.tableOpts}"
                        .columns="${this.columns}"
                        .rows="${this.isLoading ? [new TableLoadingRow()] : this.rows.length > 0 ? this.rows : [new TableNoDataRow()]}"
                        @vao-table-tableopts-change="${this.onChangeTableOpts}"
                ></vao-table>
            </div>
        `;
    }
}

window.vao = window.vao || {};
window.vao.components = window.vao.components || {};
window.vao.components.PageRulesSingleTable = PageRulesSingleTable;
customElements.define('vao-page-rules-single-table', PageRulesSingleTable);
